<template>
  <b-container fluid v-if="canRead('master_bunker_log')">
    <div class="iq-card mt-3 px-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title py-3">
          <h4 class="card-title">Bunkering Log</h4>
        </div>
        <div class="text-right">
          <button-options
            :access="'master_bunker_log'"
            :hideAdd="false"
            :hideDetail="true"
            :isRefreshEnabled="true"
            :isRefreshed="isRefreshed"
            @onAddItemClicked="openAddForm"
            @onRefreshed="getData('Refreshed')"
          />
        </div>
      </div>
      <div class="iq-card-body">
        <bunkering-log-filter
          :assets="assets"
          @apply-filters="handleApplyFilters"
          @reset-filters="handleResetFilters"
          @toggle-sounding="handleToggleSounding"
        />

        <b-row>
          <b-col md="12">
            <bunkering-log-table
              :data="data"
              :is-loading="isLoading"
              :pagination="pagination"
              :bunker-unit="filters.bunkerUnit"
              :order="filters.order"
              @page-changed="onPageChanged"
              @edit="openEdit"
              @delete="openDelete"
              @view="openDetail"
              :show-sounding-data="showSoundingData"
            />
          </b-col>
        </b-row>
      </div>

      <bunkering-log-form
        :visible="displayForm"
        :is-submit="isSubmit"
        :initial-data="currentBunkeringLog"
        :assets="assets"
        :fuel-types="fuelTypes"
        :bunker-locations="bunkerLocations"
        @close="displayForm = false"
        @submit="handleFormSubmit"
      />

      <bunkering-log-detail
        :visible="displayDetail"
        :data="selected"
        @close="displayDetail = false"
      />

      <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayDeletion">
        <delete-item
          :isSubmit="isSubmit"
          @onCancelDeletion="displayDeletion = false"
          @onConfirmDeletion="onConfirmDeletion"
        />
      </b-modal>
    </div>
  </b-container>
</template>

<script>
import moment from 'moment';
import DeleteItem from '@/components/directives/delete-item';
import ButtonOptions from '@/components/directives/button-options';
import BunkeringLogSvc from '@/services/BunkeringLogSvc';
import AssetSvc from '@/services/AssetSvc';
import FuelTypeSvc from '@/services/FuelTypeSvc';
import BunkerLocationSvc from '@/services/BunkerLocationSvc';

import BunkeringLogFilter from './components/BunkeringLogFilter.vue';
import BunkeringLogTable from './components/BunkeringLogTable.vue';
import BunkeringLogForm from './components/BunkeringLogForm.vue';
import BunkeringLogDetail from './components/BunkeringLogDetail.vue';

export default {
  name: 'MasterBunkeringLog',
  components: {
    DeleteItem,
    ButtonOptions,
    BunkeringLogFilter,
    BunkeringLogTable,
    BunkeringLogForm,
    BunkeringLogDetail,
  },
  data() {
    return {
      data: [],
      assets: [],
      fuelTypes: [],
      bunkerLocations: [],
      currentBunkeringLog: {},
      selected: {},
      displayDeletion: false,
      displayForm: false,
      displayDetail: false, // State untuk modal detail
      isLoading: false,
      isRefreshed: false,
      isSubmit: false,
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 0,
      },
      filters: {
        dateRange: {
          start: moment().subtract(1, 'year').startOf('month').toDate(),
          end: moment().endOf('day').toDate(),
        },
        selectedAsset: 'all',
        order: 'DESC',
        bunkerUnit: 'kl',
      },
      showSoundingData: false,
    };
  },
  methods: {
    async getData(notification) {
      try {
        this.isLoading = true;
        this.isRefreshed = true;

        const params = {};
        const startDate = moment(this.filters.dateRange.start).startOf('day').unix();
        const endDate = moment(this.filters.dateRange.end).endOf('day').unix();

        if (!this.filters.selectedAsset || this.filters.selectedAsset === 'all') {
          params['finds[bklgMassId]'] = this.assets.map((asset) => asset.massId);
        } else {
          params['finds[bklgMassId][]'] = this.filters.selectedAsset;
        }

        const bunkeringLogResponse = await BunkeringLogSvc.getBunkeringLogByPeriod(startDate, endDate, params);
        let bunkeringLogData = bunkeringLogResponse.data;

        if (this.filters.order === 'ASC') {
          bunkeringLogData = bunkeringLogData.sort((a, b) => moment(a.bklgStartDatetime).unix() - moment(b.bklgStartDatetime).unix());
        } else {
          bunkeringLogData = bunkeringLogData.sort((a, b) => moment(b.bklgStartDatetime).unix() - moment(a.bklgStartDatetime).unix());
        }

        this.data = bunkeringLogData;

        if (notification) {
          this.$toast.success(notification);
        }
      } catch (e) {
        console.error('get data error : ', e);
        this.$toast.error('Something Wrong');
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    async getDropdownData() {
      try {
        const [assetResponse, fuelTypesResponse, bunkerLocationResponse] = await Promise.all([
          AssetSvc.getAssets({ params: { max: 1000 } }),
          FuelTypeSvc.getFuelType({ max: 1000 }),
          BunkerLocationSvc.getBunkerLocation({ max: 1000 }),
        ]);

        this.assets = assetResponse.data.data;
        this.fuelTypes = fuelTypesResponse.data.map((fuelType) => ({ value: fuelType.mftyId, text: fuelType.mftyName }));
        this.bunkerLocations = bunkerLocationResponse.data.map((bunkerLocation) => ({ value: bunkerLocation.mbloId, text: bunkerLocation.mbloName }));
      } catch (e) {
        console.error('get dropdown data error : ', e);
        this.$toast.error('Failed to load form data');
      }
    },
    handleApplyFilters(filters) {
      this.data = [];
      this.filters = { ...this.filters, ...filters };
      this.getData('Filter applied');
    },
    handleResetFilters() {
      this.filters = {
        dateRange: {
          start: moment().subtract(1, 'year').startOf('month').toDate(),
          end: moment().endOf('day').toDate(),
        },
        selectedAsset: 'all',
        order: 'DESC',
        bunkerUnit: 'kl',
      };
      this.getData('Filter Reset');
    },
    async handleFormSubmit(formData) {
      this.isSubmit = true;
      try {
        if (!formData.bklgId) {
          console.log('creating new bunkering log');
          console.log('form data : ', formData);
          const createResponse = await BunkeringLogSvc.createBunkeringLog(formData);
          if (createResponse.status.error === 0) {
            await this.getData('Log created successfully');
          } else {
            this.$toast.error('Failed to save data');
          }
        } else {
          console.log('updating existing bunkering log');
          console.log('form data : ', formData);
          const updateResponse = await BunkeringLogSvc.updateBunkeringLog(formData.bklgId, formData);
          if (updateResponse.status.error === 0) {
            await this.getData('Log updated successfully');
          } else {
            this.$toast.error('Gagal memperbarui data');
          }
        }
      } catch (e) {
        console.error('onSubmitClicked error : ', e);
        this.$toast.error('An error occurred while submitting the form');
      } finally {
        this.displayForm = false;
        this.isSubmit = false;
      }
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData();
    },
    openAddForm() {
      this.currentBunkeringLog = {};
      this.displayForm = true;
    },
    openEdit(data) {
      this.currentBunkeringLog = { ...this._.find(this.data, (v) => v.bklgId === data.bklgId) };
      this.displayForm = true;
    },
    openDelete(data) {
      this.displayDeletion = true;
      this.selected = data;
    },
    async onConfirmDeletion() {
      this.isSubmit = true;
      try {
        const deleteBunkeringLogResponse = await BunkeringLogSvc.deleteBunkeringLog(this.selected.bklgId);
        if (deleteBunkeringLogResponse.status.error === 0) {
          await this.$toast.success('Laporan berhasil dihapus');
          this.displayDeletion = false;
          this.pagination.current = 1;
          this.getData();
        } else {
          this.$toast.error(deleteBunkeringLogResponse.status.message);
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    openDetail(data) {
      this.selected = data;
      this.displayDetail = true;
    },
    handleToggleSounding(value) {
      this.showSoundingData = value;
    },
  },
  async mounted() {
    await this.getDropdownData();
    await this.getData();
  },
};
</script>
